// ═══════════════════════════════════════════════════════════════════
//  modules/ah/AhBoites.js
//  AhBoites
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { dateCourte, erreurDe, horodate, nomObjet, nombre, RAISONS_BOITE } from "./ah";

// ⚠ CE N'EST PAS UN JOURNAL, C'EST UN BIEN. Chaque ligne est un objet qui
// appartient à quelqu'un et qu'il n'a pas encore récupéré. La boîte ne se
// purge jamais tant qu'un objet y dort — c'est dans le schéma, et c'est
// pour ça qu'il n'y a aucun bouton de suppression sur cet écran.
export function AhBoites({ token }) {
  const [filtres, setFiltres] = useState({ joueur: "", raison: "", retirees: false });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filtres.joueur) q.set("joueur", filtres.joueur);
      if (filtres.raison) q.set("raison", filtres.raison);
      if (filtres.retirees) q.set("retirees", "1");
      q.set("page", String(page));
      const res = await fetch(`${API_BASE}/api/ah/boites?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, filtres, page]);
  useEffect(() => { load(); }, [load]);

  const changer = (k, v) => { setPage(1); setFiltres((f) => ({ ...f, [k]: v })); };

  // Une route en erreur doit AFFICHER l'erreur. C'est ce qui manquait le
  // 4 septembre : l'onglet passait la page entière en blanc.
  const erreur = loading && !data ? null : erreurDe(data);

  const champ = {
    background: THEME.surface, border: `1px solid ${THEME.border}`,
    color: THEME.text, borderRadius: 6, padding: "7px 10px", fontSize: 12,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      <div style={{ color: THEME.textMuted, fontSize: 11, lineHeight: 1.7, background: THEME.surface, borderRadius: 8, padding: "10px 14px" }}>
        Ce que les joueurs n'ont pas encore récupéré. <strong style={{ color: THEME.text }}>Ce n'est pas un journal,
        c'est un bien</strong> : chaque ligne est un objet qui appartient à quelqu'un.
        La boîte n'a ni limite de taille ni expiration, et ne se purge jamais — l'acheteur peut
        être sur l'autre serveur, hors ligne, ou avoir l'inventaire plein.
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input style={{ ...champ, minWidth: 180 }} placeholder="Pseudo ou UUID"
          value={filtres.joueur} onChange={(e) => changer("joueur", e.target.value)} />
        <select style={champ} value={filtres.raison} onChange={(e) => changer("raison", e.target.value)}>
          <option value="">Toutes les raisons</option>
          {(data?.raisons || []).map((r) => (
            <option key={r.raison} value={r.raison}>{RAISONS_BOITE[r.raison] || r.raison} ({r.total})</option>
          ))}
        </select>
        <label style={{ ...champ, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: THEME.textMuted }}>
          <input type="checkbox" checked={filtres.retirees} onChange={(e) => changer("retirees", e.target.checked)} />
          Montrer aussi ce qui a déjà été retiré
        </label>
      </div>

      {data && data.somme && (
        <div style={{ color: THEME.textMuted, fontSize: 12 }}>
          <strong style={{ color: THEME.text }}>{nombre(data.total)}</strong> ligne{data.total > 1 ? "s" : ""}
          {" · "}<strong style={{ color: THEME.text }}>{nombre(data.somme.unites)}</strong> unités
          {" · "}{nombre(data.somme.joueurs)} joueur{data.somme.joueurs > 1 ? "s" : ""}
          {data.somme.plus_ancienne && <> · la plus ancienne dort depuis le {dateCourte(data.somme.plus_ancienne)}</>}
        </div>
      )}

      {loading && !data ? (
        <div style={{ color: THEME.textMuted }}>Chargement des boîtes...</div>
      ) : erreur ? (
        <MessageErreur texte={erreur} />
      ) : data.lignes.length === 0 ? (
        <div style={{ color: THEME.emerald, background: THEME.emeraldDim, border: `1px solid ${THEME.emerald}`, borderRadius: 8, padding: "14px 18px", fontSize: 13 }}>
          ✅ {filtres.joueur || filtres.raison ? "Rien ne correspond à ces filtres." : "Aucune boîte n'attend. Tout a été récupéré."}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 660 }}>
            <thead>
              <tr style={{ color: THEME.textMuted, textAlign: "left" }}>
                {["Joueur", "Objet", "Quantité", "Pourquoi", "Déposé le", "Retiré le"].map((h) => (
                  <th key={h} style={{ padding: "7px 10px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: `1px solid ${THEME.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.lignes.map((l) => (
                <tr key={l.id} style={{ borderBottom: `1px solid ${THEME.border}`, opacity: l.retiree_le ? 0.5 : 1 }}>
                  <td style={{ padding: "7px 10px", color: THEME.text, whiteSpace: "nowrap" }}>
                    {/* Un joueur inconnu de LuckPerms garde SES objets : on */}
                    {/* montre l'UUID plutôt que de faire disparaître la ligne. */}
                    {l.pseudo || <span style={{ color: THEME.textDim, fontSize: 10 }}>{l.proprietaire_uuid}</span>}
                  </td>
                  <td style={{ padding: "7px 10px", color: THEME.text }}>{nomObjet(l.identifiant_lisible)}</td>
                  <td style={{ padding: "7px 10px", color: THEME.text, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nombre(l.quantite)}</td>
                  <td style={{ padding: "7px 10px", color: THEME.textMuted, whiteSpace: "nowrap" }}>{RAISONS_BOITE[l.raison] || l.raison}</td>
                  <td style={{ padding: "7px 10px", color: THEME.textMuted, whiteSpace: "nowrap" }} title={horodate(l.deposee_le)}>{dateCourte(l.deposee_le)}</td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                    {l.retiree_le
                      ? <span style={{ color: THEME.textMuted }} title={horodate(l.retiree_le)}>{dateCourte(l.retiree_le)}</span>
                      : <span style={{ color: THEME.amber }}>en attente</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.pages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            style={{ ...champ, cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.4 : 1 }}>← Précédent</button>
          <span style={{ color: THEME.textMuted, fontSize: 12 }}>page {data.page} sur {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
            style={{ ...champ, cursor: page >= data.pages ? "not-allowed" : "pointer", opacity: page >= data.pages ? 0.4 : 1 }}>Suivant →</button>
        </div>
      )}
    </div>
  );
}

// ── Le message d'erreur ────────────────────────────────────────────────
//  Il dit CE QUI a échoué, mot pour mot, plutôt qu'un « une erreur est
//  survenue » qui n'aide ni le staff ni celui qui devra la corriger.
export function MessageErreur({ texte }) {
  return (
    <div style={{ color: THEME.red, background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: "14px 18px", fontSize: 13 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>⛔ La route n'a pas répondu correctement</div>
      <code style={{ color: THEME.text, fontSize: 12, wordBreak: "break-word" }}>{texte}</code>
      <div style={{ color: THEME.textMuted, fontSize: 11, marginTop: 8, lineHeight: 1.6 }}>
        Si le message parle d'une table ou d'une colonne, c'est côté base. S'il parle d'un rôle,
        c'est une permission. Sinon, le journal de l'intranet en dira plus.
      </div>
    </div>
  );
}
